import Component from "@glimmer/component";
import { get } from "@ember/object";
import { htmlSafe, type TrustedHTML } from "@ember/template";

import balanceRows, { type PreserveMode } from "../modifiers/balance-rows.ts";
import styles from "./image-grid.module.css";

import type { Image } from "../image.ts";

// Re-export so consumers can import the Image type from the component.
export type { Image };

export interface ImageGridSignature<Item = Image> {
  Args: {
    /**
     * Pass a plain Image array when no per-item state is needed.
     * The block receives `[image, image]` — first param is the item, second is
     * the Image, keeping existing callers working unchanged.
     */
    images?: Image[];
    /**
     * Pass an array of arbitrary items (e.g. QueueItem[]) together with @key.
     * The block receives `[item, image]` so callers can access item state.
     */
    items?: Item[];
    /**
     * Dot-notation path to the Image on each item (e.g. "preview").
     * Required when @items is provided.
     */
    key?: string;
    preserve?: PreserveMode;
    aggressive?: boolean;
    /**
     * Base row height, as a CSS length ("160px", "10rem") or a number of px.
     * Sets the `--row-height` custom property. In `aspect-ratio` mode this is
     * a target, not an exact height: filling each row's width while keeping
     * images undistorted forces the heights to vary, so it mainly influences
     * how many images pack onto a row. In `height` mode it is exact.
     */
    rowHeight?: string | number;
    /**
     * Gap between images, as a CSS length ("1rem") or a number of px. Sets
     * the `--gap` custom property.
     */
    gap?: string | number;
  };
  Blocks: {
    /**
     * First param: the raw item (the item when using @items, the Image when
     * using @images).
     * Second param: the resolved Image (always present, used for src/dimensions).
     */
    default: [item: Item, image: Image];
  };
  Element: HTMLUListElement;
}

function aspectRatio(image: Image): TrustedHTML {
  if (image.width == null || image.height == null) {
    return htmlSafe("");
  }

  return htmlSafe(`aspect-ratio: ${image.width / image.height} / 1;`);
}

// A bare number is treated as pixels; a string is used as-is so callers can
// pass any CSS length.
function cssLength(value: string | number): string {
  return typeof value === "number" ? `${value}px` : value;
}

export default class ImageGrid<Item = Image> extends Component<ImageGridSignature<Item>> {
  get resolvedItems(): Array<{ item: Item; image: Image }> {
    if (this.args.items && this.args.key) {
      const { items, key } = this.args;

      return items
        .map((item) => ({
          item,
          image: get(item, key) as Image,
        }))
        .filter((entry) => entry.image?.src != null);
    }

    return (this.args.images ?? []).map((image) => ({
      item: image as unknown as Item,
      image,
    }));
  }

  // The @rowHeight / @gap args drive the CSS custom properties. Rendered
  // before ...attributes so a caller can still override via their own style.
  get gridStyle(): TrustedHTML {
    const rules: string[] = [];
    const { rowHeight, gap } = this.args;

    if (rowHeight != null) rules.push(`--row-height: ${cssLength(rowHeight)}`);
    if (gap != null) rules.push(`--gap: ${cssLength(gap)}`);

    return htmlSafe(rules.join("; "));
  }

  <template>
    <ul
      class={{styles.imageGrid}}
      style={{this.gridStyle}}
      ...attributes
      {{balanceRows preserve=@preserve aggressive=@aggressive}}
    >
      {{#each this.resolvedItems key="image.src" as |entry|}}
        <li style={{aspectRatio entry.image}}>
          {{yield entry.item entry.image}}
        </li>
      {{/each}}
    </ul>
  </template>
}
